import React, { createContext, useState, useContext } from "react";

/**
 * to define the shape of the context and get the payload data for the flow and handle the clear also
 */
interface Props {
  children: React.ReactNode;
}

interface FormValues {
  gitlabRepository: string;
  ticketType: string;
  linkedUserStory: number;
  description: string;
}

// Define the shape of the context
interface TicketContextType {
  generatedTicketPayload?: FormValues;
  setGenerateTicketPayload: React.Dispatch<React.SetStateAction<FormValues | undefined>>;
  content: {
    title: string;
    description: string;
  };
  setContent: React.Dispatch<React.SetStateAction<{ title: string; description: string }>>;
  history: { context: string; title: string; description: string }[];
  setHistory: React.Dispatch<
    React.SetStateAction<{ context: string; title: string; description: string }[]>
  >;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  ticketType?: string;
  setTicketType: React.Dispatch<React.SetStateAction<string | undefined>>;
}

// Provide the correct type when creating the context
const TicketContext = createContext<TicketContextType | undefined>(undefined);

/**
 * TicketContextProvider component is responsible for providing the context related to ticket generation.
 * It maintains and provides states for the generated ticket payload

 * @param {Props} props - The properties for the component, which include children elements
 */

export const TicketContextProvider: React.FC<Props> = ({ children }) => {
  const [generatedTicketPayload, setGenerateTicketPayload] = useState<FormValues>();
  const [ticketType, setTicketType] = useState<string>();

  const [content, setContent] = useState<{ title: string; description: string }>({
    title: "",
    description: ""
  });
  const [history, setHistory] = useState<{ context: string; title: string; description: string }[]>(
    []
  );

  const [isLoading, setIsLoading] = useState(false);

  return (
    <TicketContext.Provider
      value={{
        generatedTicketPayload,
        setGenerateTicketPayload,
        content,
        setContent,
        history,
        setHistory,
        isLoading,
        setIsLoading,
        ticketType,
        setTicketType
      }}
    >
      {children}
    </TicketContext.Provider>
  );
};

// Custom hook with safety check
export const useTicketContext = (): TicketContextType => {
  const context = useContext(TicketContext);
  if (!context) {
    throw new Error("useTicketContext must be used within a TicketContextProvider");
  }
  return context;
};
